import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { X, Search, Loader2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const NewTicketModal = ({ modalOpen, onClose }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [newContact, setNewContact] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) { setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", { params: { searchParam } });
        setOptions(data.contacts);
        setLoading(false);
      } catch (err) { setLoading(false); toastError(err); }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParam, modalOpen]);

  const handleClose = () => { onClose(); setSearchParam(""); setSelectedContact(null); };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", { contactId, userId: user.id, status: "open" });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) { toastError(err); }
    setLoading(false);
    handleClose();
  };

  if (!modalOpen) return (
    <ContactModal open={contactModalOpen} initialValues={newContact} onClose={() => setContactModalOpen(false)} onSave={(c) => handleSaveTicket(c.id)} />
  );

  const filteredOptions = [
    ...options,
    ...(searchParam.length >= 3 && !loading && !options.find((o) => o.name?.toLowerCase() === searchParam.toLowerCase())
      ? [{ name: searchParam, _isNew: true }]
      : []),
  ];

  return (
    <>
      <ContactModal open={contactModalOpen} initialValues={newContact} onClose={() => setContactModalOpen(false)} onSave={(c) => handleSaveTicket(c.id)} />

      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
        <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-sm mx-4">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{i18n.t("newTicketModal.title")}</h2>
            <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
              <input
                autoFocus
                type="text"
                placeholder={i18n.t("newTicketModal.fieldLabel")}
                value={searchParam}
                onChange={(e) => { setSearchParam(e.target.value); setSelectedContact(null); }}
                onKeyDown={(e) => { if (!loading && selectedContact && e.key === "Enter") handleSaveTicket(selectedContact.id); }}
                className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>
            {filteredOptions.length > 0 && (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                {filteredOptions.map((option, idx) => (
                  <button
                    key={option.id || idx}
                    onClick={() => {
                      if (option._isNew) { setNewContact({ name: option.name }); setContactModalOpen(true); }
                      else { setSelectedContact(option); }
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
                      selectedContact?.id === option.id
                        ? "bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    {option._isNew
                      ? <span><span className="text-indigo-600 font-medium">{i18n.t("newTicketModal.add")}</span> {option.name}</span>
                      : <span>{option.name} {option.number && <span className="text-slate-400 text-xs">— {option.number}</span>}</span>
                    }
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
            <button onClick={handleClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              {i18n.t("newTicketModal.buttons.cancel")}
            </button>
            <button
              onClick={() => selectedContact && handleSaveTicket(selectedContact.id)}
              disabled={!selectedContact || loading}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {i18n.t("newTicketModal.buttons.ok")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewTicketModal;
